import { useEffect, useState } from 'react';
import { api } from '../api/client';
import Icon from './Icon';
import { ICONS } from '../lib/icons';

// A modal form for provisioning a new resource. Regions and service types are
// loaded from the backend for the chosen provider.
export default function ProvisionDialog({
  providers,
  onClose,
  onCreated,
  defaultType = '',
}) {
  const [providerId, setProviderId] = useState(providers[0]?.id || '');
  const [regions, setRegions] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    name: '',
    type: defaultType,
    region: '',
    spec: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!providerId) return;
    Promise.all([api.getRegions(providerId), api.getServices(providerId)])
      .then(([regs, svcs]) => {
        setRegions(regs);
        setServices(svcs);
        setForm((f) => ({
          ...f,
          region: regs[0]?.id || '',
          // Keep a preselected type (from defaultType) if it is valid.
          type: svcs.some((s) => s.id === f.type) ? f.type : svcs[0]?.id || '',
        }));
      })
      .catch(() => {
        setRegions([]);
        setServices([]);
      });
  }, [providerId]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.createResource({ providerId, ...form });
      onCreated();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel">
        <header className="modal__header">
          <h2>Provision resource</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name={ICONS.close} />
          </button>
        </header>

        <form className="modal__body form" onSubmit={submit}>
          <label className="field">
            <span>Provider</span>
            <select
              className="input"
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
            >
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Name</span>
            <input
              className="input"
              placeholder="e.g. api-node-1"
              value={form.name}
              onChange={update('name')}
              required
            />
          </label>

          <div className="form-grid">
            <label className="field">
              <span>Service</span>
              <select className="input" value={form.type} onChange={update('type')}>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} · {s.category}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Region</span>
              <select className="input" value={form.region} onChange={update('region')}>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {/* A live region subscription has no display name — the
                        provider reports the region's code and key only — so the
                        code is the label rather than a blank option. */}
                    {r.displayName || r.id}
                    {r.home ? ' · home' : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="field">
            <span>Spec / shape</span>
            <input
              className="input"
              placeholder="e.g. 2 vCPU · 8 GiB"
              value={form.spec}
              onChange={update('spec')}
            />
          </label>

          {error ? <div className="form-error">{error}</div> : null}

          <footer className="modal__footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? 'Provisioning…' : 'Provision'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
